import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import { getSeenVerifiedKeys, markSeenVerifiedKeys } from '@/dom/notificationSeen';
import type { BrandCoverageEntry } from '@/types/domain';

function keyFor(e: BrandCoverageEntry): string {
  return `${e.brandSlug}|${e.audience}|${e.category}`;
}

/**
 * Surfaces brand/audience/category combinations that became verified since
 * this device last checked. On a device's very first check, the entire
 * current catalog is treated as the baseline (marked seen immediately)
 * rather than shown as "new" — otherwise every first-time visitor would see
 * a misleadingly large notification count for brands that aren't new at all.
 */
export function useNewlyVerified() {
  const [newItems, setNewItems] = useState<BrandCoverageEntry[]>([]);
  const [allVerifiedKeys, setAllVerifiedKeys] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.getCoverage().then((res) => {
      if (cancelled || !res.success) return;
      const verified = res.data.filter((e) => e.status === 'verified');
      const verifiedKeys = verified.map(keyFor);
      setAllVerifiedKeys(verifiedKeys);

      const seen = getSeenVerifiedKeys();
      if (seen === null) {
        markSeenVerifiedKeys(verifiedKeys);
        return;
      }
      const seenSet = new Set(seen);
      setNewItems(verified.filter((e) => !seenSet.has(keyFor(e))));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function markSeen() {
    markSeenVerifiedKeys(allVerifiedKeys);
    setNewItems([]);
  }

  return { newItems, markSeen };
}
